import React, { useMemo } from 'react';
import { BarChart } from '@aidkitorg/component-library';
import { SupportedDashboardComponent, TranslatedComponentResult } from '@aidkitorg/types/lib/admin-panel/dashboard';

type AdminPanelBarChartProps = {
  component: SupportedDashboardComponent & { kind: 'Bar Chart' };
  queryResult: TranslatedComponentResult;
  height?: number;
  tableCaption?: string;
};

/**
 * Checks if a string is a valid DateTime format
 * Uses stricter criteria to avoid false positives with numeric strings like "1", "2", "3"
 */
const isValidDateTime = (str: string): boolean => {
  if (!str || typeof str !== 'string') return false;

  // Must be at least 8 characters long (e.g., "1/1/2024" or "2024-1-1")
  if (str.length < 8) return false;

  // Must contain typical date separators or patterns (-, /, :, spaces, or month names)
  // This prevents pure numbers from being treated as dates
  const hasDatePattern = /[-\/:\s]|(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/i.test(str);
  if (!hasDatePattern) return false;

  // Finally check if it's actually parseable as a valid date
  const date = new Date(str);
  return date instanceof Date && !isNaN(date.getTime());
};

/**
 * Formats a DateTime string to a readable format
 * Returns MMM DD, YYYY for dates (e.g., "Dec 08, 2024"), or original string if not a valid date
 */
const formatDateTime = (str: string): string => {
  if (!isValidDateTime(str)) return str;

  const date = new Date(str);
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[date.getMonth()];
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();

  return `${month} ${day}, ${year}`;
};

export const AdminPanelBarChart: React.FC<AdminPanelBarChartProps> = ({
  component,
  queryResult,
  height = 300,
  tableCaption,
}) => {
  const { processedData, categoryKey, valueKey } = useMemo(() => {
    const primaryGroupField = component.groups[0].kind === 'Field' ? component.groups[0].field : '';
    const summaryKey = queryResult.summaryKey || '';

    // Check if we have grouped bars (secondary field in bars config)
    const barsConfig = (component as any).bars;
    const hasSecondaryGrouping = barsConfig && barsConfig.kind === 'Grouped' && barsConfig.values?.[0]?.field;

    // Check if the primary group field contains DateTime values
    const sampleValue = queryResult.data[0]?.[primaryGroupField];
    const shouldFormatDateTime = sampleValue && isValidDateTime(String(sampleValue));

    // Convert string values to numbers for proper Y-axis scaling
    let processedData = queryResult.data.map((item: Record<string, any>) => ({
      ...item,
      [summaryKey]: Number(item[summaryKey])
    }));
    let valueKeys = [summaryKey]; // Default to original summaryKey for single group

    if (hasSecondaryGrouping) {
      const secondaryField = barsConfig.values[0].field;

      // Group data by primary field and pivot secondary field
      const groupedData: Record<string, Record<string, any>> = {};
      const secondaryValueSet = new Set<string>();

      queryResult.data.forEach((row: any) => {
        const primaryValue = String(row[primaryGroupField] || 'Unknown');
        // Format DateTime if detected
        const formattedPrimaryValue = shouldFormatDateTime
          ? formatDateTime(primaryValue)
          : primaryValue;
        const secondaryValue = String(row[secondaryField] || 'null');
        const aggregateValue = Number(row[summaryKey]) || 0;

        if (!groupedData[formattedPrimaryValue]) {
          groupedData[formattedPrimaryValue] = { [primaryGroupField]: formattedPrimaryValue };
        }

        secondaryValueSet.add(secondaryValue);

        if (!groupedData[formattedPrimaryValue][secondaryValue]) {
          groupedData[formattedPrimaryValue][secondaryValue] = 0;
        }
        groupedData[formattedPrimaryValue][secondaryValue] += aggregateValue;
      });

      processedData = Object.values(groupedData);
      valueKeys = Array.from(secondaryValueSet); // Use secondary field values as value keys
    } else if (shouldFormatDateTime) {
      // Format DateTime for single series data
      processedData = processedData.map((item: Record<string, any>) => ({
        ...item,
        [primaryGroupField]: formatDateTime(String(item[primaryGroupField])),
      }));
    }

    return {
      processedData,
      categoryKey: primaryGroupField,
      valueKey: valueKeys.length === 1 ? valueKeys[0] : valueKeys,
    };
  }, [component, queryResult]);

  return (
    <BarChart
      data={processedData}
      categoryKey={categoryKey}
      valueKey={valueKey}
      xAxisLabel={component.xAxisLabel}
      yAxisLabel={component.yAxisLabel}
      height={height}
      tableCaption={tableCaption}
    />
  );
};
